import React, { useEffect, useRef, useState } from 'react';
import { type ShowNotificationOptions, useNotifications } from '@toolpad/core';
import { Stack, Typography, useTheme } from '@mui/material';
import { CustomLinearProgress } from '../custom-linear-progress/index.ts';
import { CloseIcon } from '../../assets/icons/close.tsx';
import { ScrollBox } from '@src/features/index.ts';

export type ToastOptions = Omit<ShowNotificationOptions, 'onAction' | 'actionText' | 'severity'> & {
  title: string;
  actions?: React.ReactNode;
  message?: string;
  severity?: 'success' | 'error';
};

export type CustomToastProps = ToastOptions & {
  actions?: React.ReactNode;
  duration: number;
  notifyKey?: string;
};
const getCurrentDate = () => Date.now();
export const CustomToast: React.FC<CustomToastProps> = ({
                                                          title,
                                                          message,
                                                          actions,
                                                          duration,
                                                          notifyKey,
                                                          severity = 'success',
                                                        }) => {
  const { palette } = useTheme();
  const { close } = useNotifications();
  const [progress, setProgress] = useState(100);
  const start = useRef(getCurrentDate());

  useEffect(() => {
    const interval = window.setInterval(() => {
      const elapsed = Date.now() - start.current;
      const remaining = Math.max(0, ((duration - elapsed) / duration) * 100);
      setProgress(remaining);

      if (remaining <= 0) {
        window.clearInterval(interval);
      }
    }, 100);

    return () => window.clearInterval(interval);
  }, [duration]);
  const isError = severity === 'error';
  const progressSx =
    isError
      ? {
        backgroundColor: 'inherit',
        width: '100%',
        '& .MuiLinearProgress-bar, & .MuiLinearProgress-bar1': {
          background: palette.error.main,
        },
      }
      : {
        backgroundColor: 'inherit',
        width: '100%',
      };

  return (
    <Stack width="100%" px={2} pt={2} borderRadius={8}>
      <Stack p={3} mb={2} gap={1}>
        <Stack direction="row" alignItems="center">
          <Typography noWrap variant="subtitle_14_24">
            {title}
          </Typography>

          <CloseIcon
            style={{ marginLeft: 'auto', cursor: 'pointer' }}
            onClick={() => notifyKey && close(notifyKey)}
          />
        </Stack>
        <ScrollBox isError={isError} sx={{ maxHeight: '300px' }}>
          <Typography sx={{ wordWrap: 'break-word' }} variant="body_14_24" color={palette.light_dark}>
            {message}
          </Typography>
        </ScrollBox>
        {actions && <Stack>{actions}</Stack>}
      </Stack>

      <CustomLinearProgress
        className="NotifyLinearProgress"
        value={progress}
        sx={progressSx}
      />
    </Stack>
  );
};
